import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { mapbox, type mapboxgl } from '../../shared/results_page/mapbox_module.js';
import {
  liveMap,
  mapAnchor,
  MapViewControls,
  useMapboxContainerResize,
  useMapboxInstance,
  useMapInitializationFailure,
  type MapboxInstance,
  type MapViewControlsCopy,
} from '../../shared/results_page/results_map.js';
import { fitPadding, overlaysKey, type MapOverlays } from '../../shared/results_page/map_fit.js';
import { useDockedPreviewBlock } from '../../shared/results_page/docked_preview.js';
import { useKeyedValue } from '../../shared/use_keyed_value.js';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import {
  MAP_SEARCH_AREA_TOP_FALLBACK,
  renderMapSearchArea,
  useMapSearchArea,
  type MapSearchArea,
  type MapSearchAreaCopy,
} from '../../shared/results_page/map_search_area.js';
import type { HotelCoordinates, HotelMapView, HotelOfferView } from '../models.js';
import {
  resolvePinPlacements,
  type PinAxis,
  type PinBox,
  type PinCandidate,
} from './hotel_map_declutter.js';
import type { HotelOfferPhotoCopy } from './hotel_offer_card.js';
import { framePoints, initialMapCoordinates } from './hotel_map_frame.js';
import {
  dockedPreview as dockedPreviewOf,
  HotelMapPreview,
  markerPreview,
  PIN_BODY_CLASS,
  useHotelPreview,
  type HotelPreview,
  dockedPreviewProps,
} from './hotel_map_preview.js';
import './hotel_results.css';
import './hotel_map.css';

export type HotelMapProps = {
  map: HotelMapView;
  offers: readonly HotelOfferView[];
  overlays?: MapOverlays;
  selectLabel: string;
  detailsLabel: string;
  /** Rótulos das setas da faixa de fotos do card do pino. */
  photoCopy?: HotelOfferPhotoCopy;
  selectedHotelId?: string;
  /** O hotel EM FOCO — o par do card em hover no dock. Transitório e por
   *  atributo (o pino destaca por CSS): seleção é `selectedHotelId`. */
  activeHotelId?: string;
  selectionLocked?: boolean;
  controls?: MapViewControlsCopy;
  /**
   * Liga o controle "buscar nesta área" (padrão Airbnb/Booking): depois de um
   * GESTO do viajante — arrastar o mapa ou mudar o zoom — surge um botão que
   * re-busca a área visível ao ser clicado. AUSENTE ⇒ nenhum listener de gesto e
   * nenhum botão: mapas de detalhe/estáticos (e o mapa `contextual`) ficam
   * intactos.
   *
   * Reenquadramento PROGRAMÁTICO do próprio componente (fit inicial, resize,
   * recentralizar) nunca arma o botão — só gesto humano. O retorno pode ser
   * assíncrono: enquanto a `Promise` não resolve o botão mostra "buscando"; ao
   * resolver ele some, A MENOS que um novo gesto tenha entrado durante a busca —
   * aí a área na tela já é outra e o botão continua oferecendo a NOVA.
   */
  onSearchArea?: (area: MapSearchArea) => void | Promise<void>;
  /** Obrigatória quando `onSearchArea` está presente: os rótulos do botão. */
  searchAreaCopy?: MapSearchAreaCopy;
  /** Coleta inicial em andamento — sem base re-ofertável ainda na tela. Enquanto
   *  TRUE, o gesto NÃO arma o botão: não existe área "reofertável" antes de haver
   *  um primeiro resultado. */
  searchAreaPending?: boolean;
  onHotelSelect(hotelId: string): void;
  onDetailsOpen(hotelId: string): void;
  /** Liga o outro sentido do par pino ↔ card: presente, o clique no pino
   *  AVISA qual hotel foi tocado e para aí — nem detalhes, nem preview. É o
   *  contrato do dock (o card do hotel entra em foco); ausente, o pino segue
   *  abrindo os detalhes, que é o contrato da bancada e do mapa cheio. */
  onHotelFocus?(hotelId: string): void;
  /**
   * A prévia ANCORADA NO PÉ do mapa (#3108, o palco map-centric do celular):
   * o toque no pino abre o card do hotel encostado na base visível do mapa —
   * logo acima da folha (`overlays.sheet`) — em vez de abrir a ficha; o pino
   * tocado fica marcado (`data-previewing`), e é o toque no CARD que leva à
   * ficha. Sem hover: no dedo não existe. Ausente ⇒ a prévia é a do mouse,
   * ancorada no pino (hover/foco), e o clique segue abrindo a ficha.
   */
  dockedPreview?: {
    /** O X do card ("Fechar a prévia do hotel"). */
    readonly closeLabel: string;
    /** Avisa qual hotel está em prévia (`undefined` ao fechar) — é o que deixa
     *  o palco recolher a folha para o card caber. */
    readonly onOpenChange?: (hotelId: string | undefined) => void;
  };
};

function hasCoordinates(
  offer: HotelOfferView,
): offer is HotelOfferView & { coordinates: NonNullable<HotelOfferView['coordinates']> } {
  return Boolean(
    offer.coordinates &&
    Number.isFinite(offer.coordinates.latitude) &&
    Number.isFinite(offer.coordinates.longitude),
  );
}

function hasMapCoordinates(map: HotelMapView, offers: readonly HotelOfferView[]): boolean {
  return Boolean(
    offers.some(hasCoordinates) ||
    (map.center && Number.isFinite(map.center.latitude) && Number.isFinite(map.center.longitude)),
  );
}

function canRenderHotelMap(
  map: HotelMapView,
  accessToken: string | undefined,
  initializationFailed: boolean,
  offers: readonly HotelOfferView[],
): boolean {
  return (
    map.available && Boolean(accessToken) && !initializationFailed && hasMapCoordinates(map, offers)
  );
}

function shortHotelName(name: string): string {
  return name.replace(/^hotel\s+/i, '').split(/\s+/)[0] ?? name;
}

function initialMapZoom(map: HotelMapView, offerCount: number): number {
  return offerCount > 1 || (Boolean(map.center) && offerCount > 0) ? 13 : 14;
}

/** Um punhado de hotéis na mesma quadra levaria o `fitBounds` ao nível da rua,
 *  onde some a referência da cidade. */
const MAP_FIT_MAX_ZOOM = 15;

/** Viagem da câmera quando o destino muda no meio da conversa (trocar a cidade
 *  na barra de busca). Curta o bastante para não segurar a leitura das ofertas
 *  novas, longa o bastante para o olho seguir de onde para onde o mapa foi. */
const MAP_CAMERA_EASE_MS = 700;

const PIN_NAME_CLASS = 'hui-hotel-map-pin-name';

function pinPart(className: string): HTMLSpanElement {
  const part = document.createElement('span');
  part.className = className;
  return part;
}

/**
 * As peças do pino, em HTML — a receita oficial da Mapbox para marcador
 * customizado (`new Marker({ element })` com elemento nosso).
 *
 * **HTML e não imagem** porque o pino carrega preço, nome curto e o escudo de
 * política: em imagem seria um sprite por combinação. Com elemento ele usa os
 * mesmos tokens do card — o preço no mapa sai na mesma família e no mesmo tom
 * do preço no card, que é o que impede o mapa de parecer widget de terceiro.
 *
 * Duas peças em volta de um botão de tamanho ZERO, que é quem senta na
 * coordenada: a pílula suspensa e o bico que aponta o ponto exato.
 *
 * A fórmula da pílula é a do protótipo: `{glifo do eixo} {preço · nome curto}
 * {escudo se fora da política}`. O glifo entra por `data-glyph` e é desenhado
 * em máscara no CSS, não em componente — o marcador recebe elemento cru, sem
 * React. As palavras dos dois glifos vivem no `aria-label` do pino.
 */
function pinParts(offer: HotelOfferView): HTMLSpanElement[] {
  const body = pinPart(PIN_BODY_CLASS);
  const axis = offer.badges[0]?.icon;
  if (axis) {
    const glyph = pinPart('hui-hotel-map-pin-glyph');
    glyph.dataset.glyph = axis;
    body.append(glyph);
  }
  // O nome é PEÇA e não texto no meio do preço: é ele que a pílula curta
  // esconde quando o espaço aperta, e tê-lo separado é o que permite medir as
  // duas larguras numa passada só — a curta é a inteira menos esta caixa.
  const price = pinPart('hui-hotel-map-pin-price');
  price.textContent = offer.priceLabel;
  const name = pinPart(PIN_NAME_CLASS);
  name.textContent = ` · ${shortHotelName(offer.name)}`;
  price.append(name);
  body.append(price);
  if (offer.outOfPolicyLabel) body.append(pinPart('hui-hotel-map-pin-shield'));
  return [body, pinPart('hui-hotel-map-pin-tail')];
}

/** A caixa de um elemento vivo, no mesmo espaço que o `map.project` devolve
 *  (px a partir do canto do contêiner) — serve para a âncora, que o Mapbox
 *  posiciona e o declutter nunca rebaixa. */
function elementBox(container: HTMLElement, element: Element): PinBox {
  const box = element.getBoundingClientRect();
  const frame = container.getBoundingClientRect();
  return {
    x1: box.left - frame.left,
    y1: box.top - frame.top,
    x2: box.right - frame.left,
    y2: box.bottom - frame.top,
  };
}

/**
 * O DECLUTTER — pílulas que colidem encolhem, e as que ainda não cabem vão
 * para trás de quem ganhou o espaço.
 *
 * Com o mapa afastado as pílulas se empilham e o mapa vira ruído. O padrão de
 * mercado (Airbnb, Google Hotels) recolhe o perdedor a um ponto; aqui não,
 * porque o ponto apaga o preço, que é o número que a pessoa veio comparar — e
 * ele só aparece no aglomerado, que é exatamente quando ela mais precisa dele.
 * Quem perde mantém a pílula do preço, em segundo plano, e volta inteiro
 * quando o zoom abre espaço ou quando o pino recebe foco (hover e seleção
 * promovem por CSS, sem re-rodar nada aqui).
 *
 * Aqui só se MEDE e se aplica; quem decide é `resolvePinPlacements`, e é lá
 * que moram as duas regras (a fila por eixo e o degrau da pílula curta).
 *
 * A conta é feita a cada `idle`. Pan é translação uniforme: desloca todos os
 * retângulos juntos e não muda nenhuma interseção.
 */
function regroupPins(
  mapInstance: mapboxgl.Map,
  container: HTMLElement,
  pins: readonly {
    element: HTMLElement;
    coordinates: NonNullable<HotelOfferView['coordinates']>;
    axis?: PinAxis;
  }[],
  anchor: HTMLElement | undefined,
): void {
  const candidates: PinCandidate[] = [];
  for (const [index, pin] of pins.entries()) {
    // O nome volta ANTES de qualquer medida, e a ordem aqui é o cerne: quem
    // chega nesta passada já curto mediria a pílula CURTA como se fosse a
    // inteira, e daí subtrairia a largura do nome — devolvendo uma caixa menor
    // que a real duas vezes. O `resolvePinPlacements` então daria `full` a um
    // pino que, com o nome de volta, ainda colide. Ler `offsetWidth` já força o
    // layout, então a limpeza feita na linha de cima vale para a linha de baixo.
    delete pin.element.dataset.short;
    const body = pin.element.querySelector<HTMLElement>(`.${PIN_BODY_CLASS}`);
    const fullWidth = body?.offsetWidth ?? 0;
    // Largura 0 = o CSS ainda não aplicou (ou não há layout, como no jsdom):
    // degrada para no-op, nunca para pontos errados.
    if (fullWidth <= 0) return;
    const nameWidth =
      pin.element.querySelector<HTMLElement>(`.${PIN_NAME_CLASS}`)?.offsetWidth ?? 0;
    const point = mapInstance.project([
      pin.coordinates.longitude,
      pin.coordinates.latitude,
    ]) as unknown as { x: number; y: number };
    candidates.push({
      id: String(index),
      point,
      fullWidth,
      shortWidth: fullWidth - nameWidth,
      ...(pin.axis ? { axis: pin.axis } : {}),
    });
  }

  /* ponytail: O(n²) — grade espacial se o catálogo passar de ~200 pinos. */
  const placements = resolvePinPlacements(
    candidates,
    anchor ? [elementBox(container, anchor)] : [],
  );
  pins.forEach((pin, index) => {
    const placement = placements.get(String(index)) ?? 'full';
    // Empilhado TAMBÉM é curto: em repouso, quem foi para trás mostra o preço,
    // e o nome sairia pela lateral de quem está na frente sem nunca ser lido
    // inteiro. Sob hover ou seleção o CSS devolve o nome — aí o pino está na
    // frente de todos e há espaço para ele.
    pin.element.dataset.stacked = String(placement === 'stacked');
    if (placement === 'full') delete pin.element.dataset.short;
    else pin.element.dataset.short = 'true';
  });
}

/** Liga o declutter no mapa vivo e devolve o desligar.
 *
 *  O gatilho é `idle` e não `zoomend` porque ele fecha TAMBÉM o primeiro
 *  quadro e o `fitBounds` — é aí que as larguras das pílulas passam a existir,
 *  e sem isso a primeira vista, que é a que motivou o declutter, nasceria
 *  empilhada. Pan também dispara `idle` e o resultado não muda (translação
 *  uniforme não cria interseção), mas recalcular algumas dezenas de retângulos
 *  custa menos que orquestrar três gatilhos. */
function attachDeclutter(
  mapInstance: mapboxgl.Map,
  container: HTMLElement,
  pins: readonly { element: HTMLElement; coordinates: HotelCoordinates; axis?: PinAxis }[],
  anchor: HTMLElement | undefined,
): () => void {
  const regroup = () => {
    regroupPins(mapInstance, container, pins, anchor);
  };
  mapInstance.on('idle', regroup);
  // E uma vez AGORA. O `idle` sozinho não cobria a primeira vista: quando o
  // mapa já tinha assentado antes deste efeito rodar — que é o caso comum, os
  // marcadores entram num mapa vivo —, o próximo `idle` só vinha no primeiro
  // gesto do usuário, e até lá as pílulas nasciam empilhadas. Medido: pílulas
  // com 113px de largura e nenhum `data-collapsed` escrito. Aqui as caixas já
  // existem (o Mapbox anexou os elementos acima), então a medida é boa.
  regroup();
  return () => {
    mapInstance.off('idle', regroup);
  };
}

/** Põe âncora e pinos no mapa e devolve a limpeza — ou `undefined` quando a
 *  construção falha, e aí quem chama derruba o mapa pela metade. */
function mountMarkers({
  mapInstance,
  container,
  anchor,
  offers,
  detailsLabel,
  selectedHotelId,
  selectionLocked,
  onPinsChange,
  onPreviewOpen,
  onPreviewClose,
  onDetailsOpen,
  onHotelFocus,
  docked,
}: {
  mapInstance: mapboxgl.Map;
  container: HTMLDivElement;
  anchor: Pick<HotelMapView, 'anchorLabel' | 'center'>;
  offers: readonly (HotelOfferView & { coordinates: HotelCoordinates })[];
  detailsLabel: string;
  selectedHotelId?: string;
  selectionLocked: boolean;
  onPinsChange: (elements: readonly HTMLElement[]) => void;
  onPreviewOpen: (preview: HotelPreview) => void;
  onPreviewClose: () => void;
  onDetailsOpen: (hotelId: string) => void;
  onHotelFocus?: (hotelId: string) => void;
  docked: boolean;
}): (() => void) | undefined {
  const markers: mapboxgl.Marker[] = [];
  const pins: { element: HTMLElement; coordinates: HotelCoordinates; axis?: PinAxis }[] = [];
  try {
    let anchorElement: HTMLElement | undefined;
    const { anchorLabel, center } = anchor;
    if (anchorLabel && center) {
      anchorElement = mapAnchor(anchorLabel);
      markers.push(
        new (mapbox().Marker)({ element: anchorElement, anchor: 'bottom' })
          .setLngLat([center.longitude, center.latitude])
          .addTo(mapInstance),
      );
    }
    offers.forEach((offer) => {
      const marker = hotelMarker({
        mapInstance,
        offer,
        detailsLabel,
        selectedHotelId,
        selectionLocked,
        onPreviewOpen,
        onPreviewClose,
        onDetailsOpen,
        ...(onHotelFocus ? { onHotelFocus } : {}),
        docked,
      });
      markers.push(marker);
      const axis = offer.badges[0]?.icon;
      pins.push({
        element: marker.getElement(),
        coordinates: offer.coordinates,
        ...(axis ? { axis } : {}),
      });
    });
    const detachDeclutter = attachDeclutter(mapInstance, container, pins, anchorElement);
    onPinsChange(pins.map(({ element }) => element));
    return () => {
      detachDeclutter();
      onPinsChange([]);
      markers.forEach((marker) => marker.remove());
    };
  } catch {
    markers.forEach((marker) => marker.remove());
    return undefined;
  }
}

function hotelMarker({
  mapInstance,
  offer,
  detailsLabel,
  selectedHotelId,
  selectionLocked,
  onPreviewOpen,
  onPreviewClose,
  onDetailsOpen,
  onHotelFocus,
  docked,
}: {
  mapInstance: mapboxgl.Map;
  offer: HotelOfferView & { coordinates: NonNullable<HotelOfferView['coordinates']> };
  detailsLabel: string;
  selectedHotelId?: string;
  selectionLocked: boolean;
  onPreviewOpen: (preview: HotelPreview) => void;
  onPreviewClose: () => void;
  onDetailsOpen: (hotelId: string) => void;
  onHotelFocus?: (hotelId: string) => void;
  /** A prévia ancorada no pé (`dockedPreview`): o toque no pino abre o card. */
  docked: boolean;
}): mapboxgl.Marker {
  const element = document.createElement('button');
  element.type = 'button';
  // Sem detalhes o pino não tem para onde levar — a menos que o destino seja o
  // CARD do dock (`onHotelFocus`), que existe independente da ficha do hotel.
  element.disabled = selectionLocked || (!onHotelFocus && !offer.detailsAvailable);
  element.className = 'hui-hotel-map-pin';
  element.dataset.hotelId = offer.id;
  element.dataset.selected = String(offer.id === selectedHotelId);
  // Os glifos da pílula vêm sem palavra, então os nomes deles entram AQUI:
  // escudo que avisa "fora da política" — ou marca que diz "recomendado" — só
  // para quem enxerga não avisa nada.
  const axisNote = offer.badges[0] ? ` · ${offer.badges[0].label}` : '';
  const policyNote = offer.outOfPolicyLabel ? ` · ${offer.outOfPolicyLabel}` : '';
  // Sem `title`: o tooltip nativo dublava o card de preview no hover (achado da
  // validação de design), e o nome acessível já mora no `aria-label` acima.
  // No dock o pino não abre a ficha — aponta o card —, então o nome acessível
  // é só o hotel, sem prometer um verbo que o clique não cumpre.
  const action = onHotelFocus ? '' : `${detailsLabel} — `;
  element.setAttribute('aria-label', `${action}${offer.name}${axisNote}${policyNote}`);
  element.setAttribute('aria-current', offer.id === selectedHotelId ? 'true' : 'false');
  element.append(...pinParts(offer));
  const openPreview = () => {
    if (!selectionLocked && offer.detailsAvailable) {
      onPreviewOpen(markerPreview(element));
    }
  };
  // Sem hover na prévia ancorada no pé: ela é do TOQUE, e um card que abre ao
  // passar o mouse e fecha ao sair brigaria com o que o dedo abriu.
  if (!docked) {
    element.addEventListener('mouseenter', openPreview);
    element.addEventListener('mouseleave', onPreviewClose);
    element.addEventListener('focus', openPreview);
    element.addEventListener('blur', onPreviewClose);
  }
  element.addEventListener('click', (event) => {
    event.stopPropagation();
    // A prévia ANCORADA NO PÉ (#3108): o pino só mostra o card; a ficha é um
    // toque adiante, no card. Vale para qualquer ponteiro.
    if (docked) {
      if (!selectionLocked) onPreviewOpen(dockedPreviewOf(offer.id));
      return;
    }
    // O PAR PINO ↔ CARD do dock: o pino aponta o card do mesmo hotel, e quem
    // leva aos detalhes é o botão do card, um toque adiante. Vale para qualquer
    // ponteiro — no mouse o preview do hover continua sendo a ponte para a
    // ficha, e no toque é o único caminho que mostra o card antes de entrar.
    if (onHotelFocus) {
      onPreviewClose();
      onHotelFocus(offer.id);
      return;
    }
    // O DEDO NO PINO COBERTO abre o card, não a gaveta. Toque não tem hover,
    // então o preview — que no mouse é o passo entre ver e entrar — nunca
    // acontecia no celular; e um pino que está atrás de outro mostra o preço
    // mas não o resto, então entrar direto seria escolher no escuro. A saída
    // para os detalhes segue no botão do próprio card, um toque adiante.
    //
    // O gatilho é o PONTEIRO do evento e não a largura da tela: notebook com
    // tela sensível usa os dois, e uma `@media (pointer: coarse)` decidiria
    // pelo aparelho o que é do gesto. Pino à frente segue indo direto — ali a
    // pílula está inteira na tela.
    // Lido como PROPRIEDADE e não por `instanceof PointerEvent`: o clique
    // sintético de um teste (ou de qualquer script que não compartilhe o
    // realm da página) é um PointerEvent de verdade e mesmo assim falha o
    // `instanceof`. A propriedade responde igual nos dois casos.
    const touched = 'pointerType' in event && event.pointerType === 'touch';
    if (touched && element.dataset.stacked === 'true') {
      openPreview();
      return;
    }
    onPreviewClose();
    onDetailsOpen(offer.id);
  });
  const marker = new (mapbox().Marker)({ element })
    .setLngLat([offer.coordinates.longitude, offer.coordinates.latitude])
    .addTo(mapInstance);
  element.setAttribute('role', 'button');
  return marker;
}

function renderHotelMapControls({
  controls,
  contextual,
  mapRef,
  onRecenter,
  onGesture,
}: {
  readonly controls?: HotelMapProps['controls'];
  readonly contextual: boolean;
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  /** "Reenquadrar" REFAZ o enquadramento dos hotéis (`useMapboxFitToOffers`),
   *  animado — não vai ao pino da cidade no zoom inicial, que era o que
   *  deixava o aglomerado fora da vista (#3217 C5). */
  readonly onRecenter: () => void;
  /** Os botões `+`/`−` são gesto do viajante tanto quanto a pinça — mas
   *  `zoomIn`/`zoomOut` são chamadas PROGRAMÁTICAS, então o `moveend` delas não
   *  carrega `originalEvent` e o detector de gesto as ignoraria. Por isso o
   *  clique marca o gesto aqui, à mão. Recentralizar NÃO chama: é reenquadramento
   *  do próprio componente, e deve permanecer silencioso. */
  readonly onGesture?: () => void;
}): ReactNode {
  if (!controls || contextual) return null;
  return (
    <MapViewControls
      controls={controls}
      onZoomIn={() => {
        mapRef.current?.zoomIn();
        onGesture?.();
      }}
      onZoomOut={() => {
        mapRef.current?.zoomOut();
        onGesture?.();
      }}
      onRecenter={onRecenter}
    />
  );
}

/**
 * Enquadra a câmera no conjunto — hotéis mais o compromisso — em vez de confiar
 * num zoom fixo sobre um ponto médio. Com o zoom fixo os pinos das pontas
 * caíam FORA da caixa do mapa e o `overflow: hidden` os comia; era o que fazia
 * o zoom parecer "não reposicionar" (a projeção estava certa, o enquadramento é
 * que nunca coube). Roda só quando o conjunto muda de fato: `signature` é o que
 * segura o efeito, porque `map.center` chega como objeto novo a cada render e
 * re-enquadrar em todo render arrancaria a câmera da mão de quem está
 * navegando. Um ponto só (a âncora do destino, sem lista em cena) não tem caixa
 * para enquadrar e vai por `easeTo` no zoom de cidade — `fitBounds` de um ponto
 * vai ao nível do quarteirão.
 */
function useMapboxFitToOffers({
  anchorZoom,
  canRenderMap,
  containerRef,
  contextual,
  map,
  mapRef,
  instance,
  offers,
  overlays,
}: {
  readonly anchorZoom: number;
  readonly canRenderMap: boolean;
  readonly containerRef: { readonly current: HTMLDivElement | null };
  readonly contextual: boolean;
  readonly map: HotelMapView;
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  readonly instance: MapboxInstance | null;
  readonly offers: readonly (HotelOfferView & {
    coordinates: NonNullable<HotelOfferView['coordinates']>;
  })[];
  readonly overlays: MapOverlays;
}): { refit: () => void; onResize: () => void } {
  const points = useKeyedValue(framePoints(map, offers));
  // Mesmo tratamento dos pontos, e pela mesma razão: a chave é escalar (ver
  // `overlaysKey`).
  const frameOverlays = useKeyedValue(overlays, overlaysKey(overlays));
  // Read when the camera frames, so a new zoom alone never reframes.
  const readAnchorZoom = useStableCallback(() => anchorZoom);
  /* O enquadramento corrente, para o "Reenquadrar" repetir — sempre animado,
     porque aí a câmera já está em algum lugar e o corte seco desorienta.
     `liveFitRef` só enquanto o efeito que o criou está de pé: é o do resize. */
  const fitRef = useRef<((animated: boolean) => void) | null>(null);
  const liveFitRef = useRef<((animated: boolean) => void) | null>(null);
  // O primeiro enquadramento de cada instância é instantâneo — animar a chegada
  // de um mapa que acabou de nascer só atrasa a leitura. Da segunda em diante o
  // que mudou foi o DESTINO (trocar a cidade na busca é o caso), e aí o corte
  // seco desorienta: a câmera viaja. The ref holds the instance already framed.
  const framedRef = useRef<MapboxInstance | null>(null);
  const keepFrame = map.keepFrame === true;
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    const container = containerRef.current;
    if (!canRenderMap || !mapInstance || !container || contextual || points.length === 0)
      return undefined;
    const first = points[0];
    if (!first) return undefined;
    // Um ponto só não tem caixa para enquadrar (`fitBounds` de um ponto vai ao
    // nível do quarteirão): é a âncora do destino, e a câmera vai até ela no zoom
    // de cidade. Acontece na busca em voo, quando a lista anterior já saiu de cena.
    const fit = (animated: boolean) => {
      const duration = animated ? MAP_CAMERA_EASE_MS : 0;
      if (points.length < 2) {
        mapInstance.easeTo({
          center: first as [number, number],
          zoom: readAnchorZoom(),
          duration,
        });
        return;
      }
      const bounds = points.reduce(
        (box, point) => box.extend(point as [number, number]),
        new (mapbox().LngLatBounds)(first as [number, number], first as [number, number]),
      );
      mapInstance.fitBounds(bounds, {
        padding: fitPadding(container, frameOverlays),
        maxZoom: MAP_FIT_MAX_ZOOM,
        duration,
      });
    };
    const animated = framedRef.current === instance;
    framedRef.current = instance;
    fitRef.current = fit;
    liveFitRef.current = fit;
    const release = () => {
      if (liveFitRef.current === fit) liveFitRef.current = null;
    };
    // A câmera é a área que a pessoa buscou: o "Reenquadrar" segue valendo para o
    // conjunto novo, mas a chegada dele não arranca o mapa da mão dela (#3984).
    if (animated && keepFrame) return release;
    // Duas vezes de propósito. A primeira evita um quadro sem enquadramento; a
    // segunda é a que vale, porque só depois do `load` (e do `resize()` que
    // corre nele) o mapa conhece o tamanho real do contêiner — enquadrar com a
    // medida velha devolve exatamente o defeito que estamos consertando. A
    // repetição é imperceptível: o `load` só existe na instância nova, e lá a
    // duração ainda é 0.
    fit(animated);
    const onLoad = () => {
      fit(false);
    };
    mapInstance.on('load', onLoad);
    return () => {
      release();
      mapInstance.off('load', onLoad);
    };
  }, [
    canRenderMap,
    containerRef,
    contextual,
    frameOverlays,
    instance,
    keepFrame,
    mapRef,
    points,
    readAnchorZoom,
  ]);
  const refit = useCallback(() => {
    fitRef.current?.(true);
  }, []);
  // O contêiner assentou num tamanho novo (ver `useMapboxContainerResize`): o
  // enquadramento corrente se refaz, menos quando a câmera é da pessoa.
  const onResize = useCallback(() => {
    if (!keepFrame) liveFitRef.current?.(true);
  }, [keepFrame]);
  return { refit, onResize };
}

/** O foco é ATRIBUTO no pino que já existe, não pino novo: remontar o marcador
 *  troca o HTML por baixo do cursor e perde o hover no meio do gesto — e o hover
 *  do dock é justamente o que precisa sobreviver. */
function useHotelMapPinFlag(
  pinElements: readonly HTMLElement[],
  flag: 'active' | 'previewing',
  hotelId: string | undefined,
): void {
  useEffect(() => {
    pinElements.forEach((element) => {
      element.dataset[flag] = String(hotelId !== undefined && element.dataset.hotelId === hotelId);
    });
  }, [flag, hotelId, pinElements]);
}

/** A prévia ancorada no pé (#3108) refletida para fora: o pino em prévia fica
 *  marcado (`data-previewing`) e o chamador sabe qual hotel está aberto — é o
 *  que deixa o palco recolher a folha. Só com `dockedPreview` ligado. */
function useDockedPreviewSync(
  pinElements: readonly HTMLElement[],
  dockedPreview: HotelMapProps['dockedPreview'],
  previewHotelId: string | undefined,
): void {
  const docked = dockedPreview !== undefined;
  useHotelMapPinFlag(pinElements, 'previewing', docked ? previewHotelId : undefined);
  const previewOpenChange = useStableCallback((hotelId: string | undefined) => {
    dockedPreview?.onOpenChange?.(hotelId);
  });
  useEffect(() => {
    if (docked) previewOpenChange(previewHotelId);
  }, [docked, previewHotelId, previewOpenChange]);
}

/** Monta âncora e pinos no mapa vivo e derruba o mapa pela metade quando a
 *  construção falha (`mountMarkers` devolve `undefined`). Extraído do corpo do
 *  `HotelMap` como os demais `useMapbox*`: é o mesmo padrão de "efeito de mapa
 *  com muitas dependências vira hook nomeado". */
function useHotelMapMarkers({
  instance,
  canRenderMap,
  containerRef,
  contextual,
  detailsLabel,
  map,
  mapRef,
  offers,
  openDetails,
  focusHotel,
  pinFocusEnabled,
  openPreview,
  schedulePreviewClose,
  selectedHotelId,
  selectionLocked,
  setInitializationFailed,
  setPinElements,
  docked,
}: {
  /** Muda a cada mapa novo (token ou controles trocados): o mapa recriado nasce sem pinos. */
  readonly instance: MapboxInstance | null;
  readonly canRenderMap: boolean;
  readonly containerRef: { readonly current: HTMLDivElement | null };
  readonly contextual: boolean;
  readonly detailsLabel: string;
  readonly map: HotelMapView;
  readonly mapRef: { current: mapboxgl.Map | null };
  readonly offers: readonly (HotelOfferView & { coordinates: HotelCoordinates })[];
  readonly openDetails: (hotelId: string) => void;
  readonly focusHotel: (hotelId: string) => void;
  /** Se o pino aponta o card (`onHotelFocus` presente). Boolean à parte porque
   *  `focusHotel` é evento de efeito — estável, não remonta nada sozinho — e a
   *  troca de contrato tem de remontar os marcadores. */
  readonly pinFocusEnabled: boolean;
  readonly openPreview: (preview: HotelPreview) => void;
  readonly schedulePreviewClose: () => void;
  readonly selectedHotelId?: string;
  readonly selectionLocked: boolean;
  readonly setInitializationFailed: (failed: boolean) => void;
  readonly setPinElements: (elements: readonly HTMLElement[]) => void;
  readonly docked: boolean;
}): void {
  // `map` inteiro reconstrói a cada render; só anchorLabel/center movem o pino.
  const { anchorLabel, center } = map;
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    const container = containerRef.current;
    if (!canRenderMap || !mapInstance || !container || contextual) return undefined;
    const cleanup = mountMarkers({
      mapInstance,
      container,
      anchor: { anchorLabel, center },
      offers,
      detailsLabel,
      selectedHotelId,
      selectionLocked,
      onPinsChange: setPinElements,
      onPreviewOpen: openPreview,
      onPreviewClose: schedulePreviewClose,
      onDetailsOpen: openDetails,
      ...(pinFocusEnabled ? { onHotelFocus: focusHotel } : {}),
      docked,
    });
    if (cleanup) return cleanup;
    if (mapRef.current === mapInstance) {
      mapRef.current = null;
      mapInstance.remove();
    }
    setInitializationFailed(true);
    return undefined;
  }, [
    instance,
    canRenderMap,
    containerRef,
    contextual,
    detailsLabel,
    docked,
    anchorLabel,
    center,
    mapRef,
    offers,
    openDetails,
    focusHotel,
    openPreview,
    pinFocusEnabled,
    schedulePreviewClose,
    selectedHotelId,
    selectionLocked,
    setInitializationFailed,
    setPinElements,
  ]);
}

/** Com o `mapbox-gl` sob demanda (#5796), o primeiro mapa da página nasce depois do
 *  primeiro render: os pinos esperam o aviso do mapa (`instance`) e montam uma vez só. */
const mapAnnounced = (canRender: boolean, instance: MapboxInstance | null) =>
  canRender && instance !== null;

export function HotelMap({
  map,
  offers,
  overlays = {},
  selectLabel,
  detailsLabel,
  photoCopy,
  selectedHotelId,
  activeHotelId,
  selectionLocked = false,
  controls,
  onSearchArea,
  searchAreaCopy,
  searchAreaPending = false,
  onHotelSelect,
  onDetailsOpen,
  onHotelFocus,
  dockedPreview,
}: Readonly<HotelMapProps>): ReactNode {
  const docked = dockedPreview !== undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const accessToken = map.accessToken?.trim();
  const [initializationFailed, setInitializationFailed] = useMapInitializationFailure(
    JSON.stringify([map.available, accessToken]),
  );
  // Os pinos vivos em ESTADO e não em ref: quem acende o foco precisa acordar
  // quando os marcadores são remontados (troca de seleção, de ofertas), senão o
  // pino novo nasce apagado no meio do gesto.
  const [pinElements, setPinElements] = useState<readonly HTMLElement[]>([]);
  const {
    preview,
    open: openPreview,
    close: closePreview,
    scheduleClose: schedulePreviewClose,
    cancelClose: cancelPreviewClose,
  } = useHotelPreview();
  const dockedBlock = useDockedPreviewBlock();
  const mappedOffers = useMemo(() => offers.filter(hasCoordinates), [offers]);
  const initialZoom = initialMapZoom(map, mappedOffers.length);
  const canRenderMap = canRenderHotelMap(map, accessToken, initializationFailed, offers);
  const contextual = map.contextual === true;
  const openDetails = useStableCallback(onDetailsOpen);
  const focusHotel = useStableCallback((hotelId: string) => {
    onHotelFocus?.(hotelId);
  });
  const { mapRef, instance } = useMapboxInstance({
    accessToken,
    canRenderMap,
    interactive: !contextual,
    nativeControls: controls === undefined,
    containerRef,
    initialCoordinates: initialMapCoordinates(map, mappedOffers),
    initialZoom,
    setInitializationFailed,
  });
  const searchArea = useMapSearchArea({
    canRenderMap,
    mapRef,
    instance,
    ...(onSearchArea && !contextual ? { onSearchArea } : {}),
    pending: searchAreaPending,
  });
  const { refit, onResize } = useMapboxFitToOffers({
    anchorZoom: initialZoom,
    canRenderMap,
    containerRef,
    contextual,
    mapRef,
    instance,
    offers: mappedOffers,
    map,
    overlays,
  });
  useMapboxContainerResize({ canRenderMap, containerRef, mapRef, instance, onSettle: onResize });

  useHotelMapMarkers({
    instance,
    canRenderMap: mapAnnounced(canRenderMap, instance),
    containerRef,
    contextual,
    detailsLabel,
    map,
    mapRef,
    offers: mappedOffers,
    openDetails,
    focusHotel,
    pinFocusEnabled: onHotelFocus !== undefined,
    openPreview,
    schedulePreviewClose,
    ...(selectedHotelId ? { selectedHotelId } : {}),
    selectionLocked,
    setInitializationFailed,
    setPinElements,
    docked,
  });

  useHotelMapPinFlag(pinElements, 'active', activeHotelId);
  useDockedPreviewSync(pinElements, dockedPreview, preview?.hotelId);

  if (!canRenderMap) {
    return (
      <section className="hui-hotel-map-fallback" aria-label={map.fallbackLabel}>
        <p>{map.fallbackLabel}</p>
      </section>
    );
  }

  const previewOffer = preview ? offers.find((offer) => offer.id === preview.hotelId) : undefined;

  return (
    <section
      className="hui-hotel-map"
      aria-label={map.fallbackLabel}
      data-contextual={contextual || undefined}
      style={dockedBlock.style}
    >
      <div ref={containerRef} className="hui-hotel-map-canvas" />
      {preview && previewOffer && (
        <HotelMapPreview
          preview={preview}
          offer={previewOffer}
          detailsLabel={detailsLabel}
          selectLabel={selectLabel}
          selectionLocked={selectionLocked}
          onMouseEnter={cancelPreviewClose}
          onMouseLeave={schedulePreviewClose}
          onHotelSelect={onHotelSelect}
          onDetailsOpen={onDetailsOpen}
          onClose={closePreview}
          onDockedResize={dockedBlock.onDockedResize}
          {...dockedPreviewProps(dockedPreview, overlays, photoCopy)}
        />
      )}
      {renderHotelMapControls({
        controls,
        contextual,
        mapRef,
        onRecenter: refit,
        onGesture: searchArea.markGesture,
      })}
      {renderMapSearchArea({
        searchArea,
        copy: searchAreaCopy,
        topOffset: overlays.top ?? MAP_SEARCH_AREA_TOP_FALLBACK,
      })}
    </section>
  );
}
